import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ComicCreate, PersonWithRole, Tag, Language } from '../../types';
import { createComic, downloadCover, uploadCover, getImageUrl, getPeople, getTags, getRoles } from '../../api';
import PersonRoleSelector from '../forms/PersonRoleSelector';
import TagAutocomplete from '../autocomplete/TagAutocomplete';
import LanguageDropdown from '../forms/LanguageDropdown';
import LoanedToAutocomplete from '../autocomplete/LoanedToAutocomplete';
import SeriesAutocomplete from '../autocomplete/SeriesAutocomplete';
import ScrapeModal from './ScrapeModal';
import GradeSlider from '../forms/GradeSlider';
import { useSettings } from '../../contexts/SettingsContext';
import { useReferenceData } from '../../contexts/ReferenceDataContext';
import { useFieldVisibility } from '../../hooks/useFieldVisibility';

interface AddComicModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComicAdded: () => void;
}

const getLastStatus = (): 'owned' | 'wishlist' | 'ordered' | 'loaned' => {
  const stored = localStorage.getItem('lastComicStatus');
  if (stored === 'owned' || stored === 'wishlist' || stored === 'ordered' || stored === 'loaned') {
    return stored;
  }
  return 'owned';
};

const AddComicModal: React.FC<AddComicModalProps> = ({ isOpen, onClose, onComicAdded }) => {
  const { t } = useTranslation();
  const { settings, isLLMConfigured } = useSettings();
  const { languages } = useReferenceData();
  const { isFieldVisible } = useFieldVisibility();

  // Get showAdultContent from settings context, fallback to localStorage for unauthenticated users
  const showAdultContent = settings?.show_adult_content ?? (localStorage.getItem('showAdultContent') === 'true');
  const [comic, setComic] = useState<ComicCreate>({
    title: '',
    series: '',
    issue_number: '',
    language_id: undefined,
    publication_date: '',
    cover_image: '',
    notes: '',
    status: getLastStatus(),
    loaned_to: '',
    is_adult_content: false,
    grade: null,
    person_ids: [],
    person_roles: [],
    tag_ids: [],
  });
  
  const [selectedPersonRoles, setSelectedPersonRoles] = useState<PersonWithRole[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  const [coverUrl, setCoverUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloadingCover, setIsDownloadingCover] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isScrapeModalOpen, setIsScrapeModalOpen] = useState(false);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Reset scrape modal state when this modal opens
      setIsScrapeModalOpen(false);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handlePersonRoleAdd = (personWithRole: PersonWithRole) => {
    const newPersonRoles = [...selectedPersonRoles, personWithRole];
    setSelectedPersonRoles(newPersonRoles);
    
    // Update both person_ids and person_roles
    const personIds = newPersonRoles.map(pr => pr.person.id);
    const personRoleAssignments = newPersonRoles.map(pr => ({
      person_id: pr.person.id,
      role_id: pr.role?.id || null
    }));
    
    setComic({ 
      ...comic, 
      person_ids: personIds,
      person_roles: personRoleAssignments
    });
  };

  const handlePersonRoleRemove = (index: number) => {
    const newPersonRoles = selectedPersonRoles.filter((_, i) => i !== index);
    setSelectedPersonRoles(newPersonRoles);
    
    // Update both person_ids and person_roles
    const personIds = newPersonRoles.map(pr => pr.person.id);
    const personRoleAssignments = newPersonRoles.map(pr => ({
      person_id: pr.person.id,
      role_id: pr.role?.id || null
    }));
    
    setComic({ 
      ...comic, 
      person_ids: personIds,
      person_roles: personRoleAssignments
    });
  };

  const handleTagSelect = (tag: Tag) => {
    setSelectedTags([...selectedTags, tag]);
    setComic(prevComic => ({ ...prevComic, tag_ids: [...prevComic.tag_ids, tag.id] }));
  };

  const handleTagRemove = (tagId: number) => {
    setSelectedTags(selectedTags.filter(t => t.id !== tagId));
    setComic(prevComic => ({ ...prevComic, tag_ids: prevComic.tag_ids.filter(id => id !== tagId) }));
  };


  const handleLanguageSelect = (language: Language | null) => {
    setSelectedLanguage(language);
    setComic(prevComic => ({ ...prevComic, language_id: language?.id }));
  };


  const handleDownloadCover = async () => {
    if (!coverUrl.trim()) return;
    
    setIsDownloadingCover(true);
    try {
      const result = await downloadCover(coverUrl.trim());
      setComic(prevComic => ({ ...prevComic, cover_image: result.path }));
      setCoverUrl('');
    } catch (error) {
      
      alert(t('cover.failedToDownload'));
    } finally {
      setIsDownloadingCover(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert(t('cover.pleaseSelectImageFile'));
      return;
    }

    setIsUploadingCover(true);
    try {
      const result = await uploadCover(file);
      setComic(prevComic => ({ ...prevComic, cover_image: result.path }));
    } catch (error) {
      
      alert(t('cover.failedToUpload'));
    } finally {
      setIsUploadingCover(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate and sanitize publication_date
      let validPublicationDate = comic.publication_date;
      if (validPublicationDate && validPublicationDate.trim() !== '') {
        // Only validate if there's actually a date value
        const datePattern = /^\d{4}-\d{2}-\d{2}$/;
        if (datePattern.test(validPublicationDate)) {
          // Check if it's a valid date and doesn't contain invalid months/days like "00"
          const date = new Date(validPublicationDate);
          if (isNaN(date.getTime()) || validPublicationDate.includes('-00-')) {
            validPublicationDate = undefined;
          }
        } else {
          validPublicationDate = undefined;
        }
      } else {
        // Empty or whitespace-only string should be undefined
        validPublicationDate = undefined;
      }
      
      const comicData = {
        ...comic,
        publication_date: validPublicationDate,
      };

      await createComic(comicData);
      localStorage.setItem('lastComicStatus', comic.status);
      onComicAdded();
      resetForm();
      onClose();
    } catch (error) {
      
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAIScrapedData = async (scrapedData: Partial<ComicCreate>) => {
    
    
    
    
    setComic(prevComic => ({
      ...prevComic,
      ...scrapedData
    }));

    // Update UI state variables to reflect the scraped data
    try {
      // Handle tags
      if (scrapedData.tag_ids && scrapedData.tag_ids.length > 0) {
        const allTags = await getTags();
        const selectedTagsData = allTags.filter(tag => scrapedData.tag_ids!.includes(tag.id));
        setSelectedTags(selectedTagsData);
      }

      // Handle language
      if (scrapedData.language_id) {
        // Use the language object if provided directly, otherwise look it up
        const languageObject = (scrapedData as any).language_object;
        
        if (languageObject) {
          
          setSelectedLanguage(languageObject);
        } else {
          
          const language = languages.find(l => l.id === scrapedData.language_id);
          
          if (language) {
            setSelectedLanguage(language);
          }
        }
      }

      // Handle people with roles
      if (scrapedData.person_roles && scrapedData.person_roles.length > 0) {
        const allPeople = await getPeople();
        const allRoles = await getRoles();
        
        const personWithRoles: PersonWithRole[] = scrapedData.person_roles
          .map(pr => {
            const person = allPeople.find(p => p.id === pr.person_id);
            const role = pr.role_id ? allRoles.find(r => r.id === pr.role_id) : null;
            
            if (person) {
              return { person, role: role || null };
            }
            return null;
          })
          .filter((pr): pr is PersonWithRole => pr !== null);
        
        setSelectedPersonRoles(personWithRoles);
      }
    } catch (error) {
      
      // Continue anyway - the form data is still updated
    }
  };

  const resetForm = () => {
    setComic({
      title: '',
      series: '',
      issue_number: '',
      language_id: undefined,
      publication_date: '',
      cover_image: '',
      notes: '',
      status: getLastStatus(),
      loaned_to: '',
      is_adult_content: false,
      person_ids: [],
      person_roles: [],
      tag_ids: [],
    });
    setSelectedPersonRoles([]);
    setSelectedTags([]);
    setSelectedLanguage(null);
    setCoverUrl('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border dark:border-gray-700">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('comic.addNewComic')}</h2>
            <div className="flex items-center gap-2">
              {isLLMConfigured() && (
                <button
                  type="button"
                  onClick={() => setIsScrapeModalOpen(true)}
                  className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm flex items-center gap-1"
                  title={t('comic.scrapeComicData')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {t('comic.scrapeButton')}
                </button>
              )}
              <button
                onClick={onClose}
                className="text-gray-500 dark:text-gray-400 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('comic.title')} *
              </label>
              <input
                type="text"
                required
                value={comic.title}
                onChange={(e) => setComic(prevComic => ({ ...prevComic, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('comic.status')}
              </label>
              <select
                value={comic.status}
                onChange={(e) => setComic(prevComic => ({ ...prevComic, status: e.target.value as 'owned' | 'wishlist' | 'ordered' | 'loaned' }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="owned">{t('status.owned')}</option>
                <option value="wishlist">{t('status.wishlist')}</option>
                <option value="ordered">{t('status.ordered')}</option>
                <option value="loaned">{t('status.loaned')}</option>
              </select>
            </div>

            {showAdultContent && isFieldVisible('is_adult_content') && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_adult_content"
                  checked={comic.is_adult_content || false}
                  onChange={(e) => setComic(prevComic => ({ ...prevComic, is_adult_content: e.target.checked }))}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <label htmlFor="is_adult_content" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('comic.adultContent')}
                </label>
              </div>
            )}

            {comic.status === 'loaned' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('comic.loanedTo')}
                </label>
                <LoanedToAutocomplete
                  value={comic.loaned_to || ''}
                  onChange={(value) => setComic(prevComic => ({ ...prevComic, loaned_to: value }))}
                  placeholder={t('placeholder.searchOrAddPerson')}
                />
              </div>
            )}

            {isFieldVisible('series') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('comic.series')}
                </label>
                <SeriesAutocomplete
                  value={comic.series}
                  onChange={(value) => setComic(prevComic => ({ ...prevComic, series: value }))}
                  placeholder={t('placeholder.searchOrEnterSeries')}
                />
              </div>
            )}

            {isFieldVisible('issue_number') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('comic.issueNumber')}
                </label>
                <input
                  type="text"
                  value={comic.issue_number || ''}
                  onChange={(e) => setComic(prevComic => ({ ...prevComic, issue_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.issueNumber')}
                />
              </div>
            )}

            {isFieldVisible('language_id') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('common.language')}
                </label>
                <LanguageDropdown
                  selectedLanguage={selectedLanguage}
                  onLanguageSelect={handleLanguageSelect}
                  placeholder={t('placeholder.selectLanguage')}
                />
              </div>
            )}

            {isFieldVisible('publication_date') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('comic.publicationDate')}
                </label>
                <input
                  type="date"
                  value={comic.publication_date}
                  onChange={(e) => setComic(prevComic => ({ ...prevComic, publication_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {isFieldVisible('grade') && (
              <div>
                <GradeSlider
                  grade={comic.grade ?? null}
                  onChange={(grade) => setComic(prevComic => ({ ...prevComic, grade }))}
                />
              </div>
            )}

            {isFieldVisible('cover_image') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('comic.coverImage')}
                </label>
              
              {/* File Upload Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                  isDragOver 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={isUploadingCover}
                  className="hidden"
                  id="cover-upload"
                />
                <label
                  htmlFor="cover-upload"
                  className={`cursor-pointer ${isUploadingCover ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  <div className="text-gray-600 dark:text-gray-300">
                    {isUploadingCover ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                        {t('common.uploading')}
                      </div>
                    ) : (
                      <>
                        <p className="text-sm">
                          <span className="font-medium text-blue-600">{t('cover.clickToUpload')}</span> {t('cover.dragAndDrop')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('cover.fileTypes')}</p>
                      </>
                    )}
                  </div>
                </label>
              </div>

              {/* URL Download Option */}
              <div className="mt-3">
                <div className="flex items-center mb-2">
                  <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                  <span className="px-3 text-sm text-gray-500 dark:text-gray-400">{t('cover.orDownloadFromUrl')}</span>
                  <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                </div>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={coverUrl}
                    onChange={(e) => setCoverUrl(e.target.value)}
                    placeholder={t('cover.enterImageUrl')}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={handleDownloadCover}
                    disabled={!coverUrl.trim() || isDownloadingCover}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {isDownloadingCover ? t('common.downloading') : t('cover.download')}
                  </button>
                </div>
              </div>

              {/* Preview */}
              {comic.cover_image && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-600 dark:text-gray-300">{t('cover.currentCover')}</p>
                    <button
                      type="button"
                      onClick={() => setComic(prevComic => ({ ...prevComic, cover_image: undefined }))}
                      className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                      title={t('cover.clearCoverImage')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <img
                    src={getImageUrl(comic.cover_image)}
                    alt={t('cover.coverPreview')}
                    className="h-20 w-16 object-cover rounded border"
                  />
                </div>
              )}
              </div>
            )}

            {isFieldVisible('notes') && (
              <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('comic.notes')}
              </label>
              <textarea
                value={comic.notes}
                onChange={(e) => setComic(prevComic => ({ ...prevComic, notes: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
              />
              </div>
            )}

            {isFieldVisible('people') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('comic.peopleAndRoles')}
                </label>
                <PersonRoleSelector
                  selectedPersonRoles={selectedPersonRoles}
                  onPersonRoleAdd={handlePersonRoleAdd}
                  onPersonRoleRemove={handlePersonRoleRemove}
                  placeholder={t('placeholder.addPersonAndRole')}
                />
              </div>
            )}

            {isFieldVisible('tags') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('comic.tags')}
              </label>
              <TagAutocomplete
                selectedTags={selectedTags}
                onTagSelect={handleTagSelect}
                onTagRemove={handleTagRemove}
                placeholder={t('placeholder.searchOrAddTag')}
              />
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  onClose();
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? t('comic.adding') : t('comic.addComic')}
              </button>
            </div>
          </form>
        </div>
      </div>

      <ScrapeModal
        isOpen={isScrapeModalOpen}
        onClose={() => setIsScrapeModalOpen(false)}
        onDataSelected={handleAIScrapedData}
        initialTitle={comic.title}
        initialSeries={comic.series}
        initialWriter={selectedPersonRoles[0]?.person.name || ''}
        availablePeople={selectedPersonRoles.map(pr => ({ 
          name: pr.person.name, 
          role: pr.role?.name 
        }))}
      />
    </div>
  );
};

export default AddComicModal;